import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Send } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

type Mode = 'answer' | 'lesson';

interface ChatInputRowProps {
  mode: Mode;
  onSubmit: (value: string) => void;
  onDone?: () => void;
  doneEnabled?: boolean;
  resetKey: string;
}

export function ChatInputRow({
  mode,
  onSubmit,
  onDone,
  doneEnabled = false,
  resetKey,
}: ChatInputRowProps) {
  const { t } = useTranslation('debriefV2');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue('');
    textareaRef.current?.focus();
  }, [resetKey]);

  const trimmed = value.trim();
  const canSend = trimmed.length > 0;

  const submit = () => {
    if (!canSend) return;
    onSubmit(trimmed);
    setValue('');
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const placeholder =
    mode === 'lesson'
      ? t('deepDive.inputPlaceholderLesson')
      : t('deepDive.inputPlaceholderAnswer');

  return (
    <form
      onSubmit={handleSubmit}
      className="px-5 py-4 border-t border-slate-200 bg-slate-50/40 rounded-b-2xl flex items-center gap-2 mt-auto"
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={1}
        className="flex-1 min-w-0 resize-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-[14px] text-slate-800 placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none transition-all shadow-sm"
      />

      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="submit"
          disabled={!canSend}
          className={cn(
            'px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 shadow-sm shrink-0 transition-colors border border-transparent',
            canSend
              ? 'bg-primary hover:bg-button-primary-hover text-white cursor-pointer'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200'
          )}
        >
          {t('deepDive.send')}
          <Send className="h-4 w-4" aria-hidden />
        </button>

        {mode === 'lesson' && onDone && (
          <button
            type="button"
            onClick={onDone}
            disabled={!doneEnabled}
            className={cn(
              'px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 shadow-sm transition-all border border-transparent',
              doneEnabled
                ? 'bg-primary hover:bg-button-primary-hover text-white cursor-pointer active:scale-[0.98]'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200'
            )}
          >
            {t('deepDive.done')}
            <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
          </button>
        )}
      </div>
    </form>
  );
}
